import { nl } from './locales/nl';

/**
 * The interface language (P-975). Texts are written in English in the code and wrapped in `t()`, which looks them up
 * in the chosen language's dictionary (the English text is the key, so English needs none, and a missing translation
 * shows the English). The choice is made once, when this module loads — an explicit choice stored on this device,
 * otherwise the device's own language — so texts built when a module loads are right too. Changing it reloads the
 * app, the way Notion does.
 */
export type Language = 'en' | 'nl';

export const LANGUAGES: ReadonlyArray<{ id: Language; label: string }> = [
  { id: 'en', label: 'English (US)' },
  { id: 'nl', label: 'Nederlands' },
];

const DICTIONARIES: Record<Language, Record<string, string> | null> = { en: null, nl };
const KEY = 'lumen.language';

/** A stored choice (anything else counts as none) or else the device language; Dutch for any `nl…`, otherwise English. */
export function resolveLanguage(stored: string | null | undefined, device: string | null | undefined): Language {
  if (stored === 'en' || stored === 'nl') return stored;
  return /^nl\b/i.test(device ?? '') ? 'nl' : 'en';
}

/** `text` in `lang`, with `{name}` placeholders filled in from `vars`. */
export function translate(lang: Language, text: string, vars?: Record<string, string | number>): string {
  const out = DICTIONARIES[lang]?.[text] || text;
  return vars ? out.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m)) : out;
}

function stored(): string | null {
  // Only pages store a choice (Node has a localStorage of its own that would warn on first use).
  if (typeof document === 'undefined') return null;
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

/** The device's language. Only a browser page has one: on the server every text stays English. */
function device(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  // The Mac app passes the system language (and tests can pin it); browsers and web views report their own.
  const native = (globalThis as { lumenNative?: { language?: string } }).lumenNative?.language;
  return native || navigator.language;
}

let current: Language = resolveLanguage(stored(), device());
// The page says which language it is in (screen readers, hyphenation).
if (typeof document !== 'undefined') document.documentElement.lang = current;

/** The interface language. */
export const language = (): Language => current;

/** The locale for dates and numbers: the interface's when it is Dutch, otherwise the browser's own. */
export const locale = (): string | undefined => (current === 'nl' ? 'nl-NL' : undefined);

/** `text` in the interface language. Write the English text, with `{name}` for anything filled in. */
export const t = (text: string, vars?: Record<string, string | number>): string => translate(current, text, vars);

/** A count with its noun: `plural(n, '{n} page', '{n} pages')`, each form translated whole. */
export const plural = (n: number, one: string, other: string): string => t(n === 1 ? one : other, { n });

/** Reloads after a moment, so what was changed just before (the account's setting) is stored on this device first. */
const reloadSoon = () => void setTimeout(() => location.reload(), 400);

/**
 * Makes `lang` the language on this device (null: follow the device) and reloads so every text is read again.
 * Returns whether anything changed (nothing reloads then).
 */
export function chooseLanguage(lang: Language | null, reload: () => void = reloadSoon): boolean {
  try {
    if (lang) localStorage.setItem(KEY, lang);
    else localStorage.removeItem(KEY);
  } catch {
    /* storage blocked: the choice lasts for this page only */
  }
  const next = resolveLanguage(lang, device());
  if (next === current) return false;
  current = next;
  reload();
  return true;
}
